/**
 * builderLogoOverrides
 * --------------------
 * Canonical builder-logo resolver. Always returns the freshest logo URL
 * for a known builder slug, even when the caller passes a stale
 * `logo_url` from localStorage (e.g. the FavouritesSidebar saves a
 * snapshot of `logo_url` at the time the user shortlists the builder,
 * which goes stale every time we re-issue the partner logo pack).
 *
 * Audit dates:
 *   - 2026-02-13: initial override map (six builders with broken upstream).
 *   - 2026-02-26: switched to slug → Supabase Storage map after migrating
 *     all 31 active builders to a unified `builder-logos` bucket. Every
 *     known slug now resolves to its canonical Supabase URL so a
 *     shortlist saved months ago still shows the latest partner logo.
 *
 * To refresh after a logo rotation:
 *   1. Drop the new pack into Supabase Storage (`builder-logos` bucket).
 *   2. Run `python3 backend/scripts/refresh_builder_logos.py`.
 *   3. Re-generate the `KNOWN_SLUGS` array below from the script's output.
 */

// All active builder slugs whose logos live on Supabase Storage under
// `builder-logos/<slug>.png`. Keep in sync with the builders table.
const SUPABASE_URL = (
  process.env.REACT_APP_SUPABASE_URL ||
  'https://ghkultcdfnneodcjmxpn.supabase.co'
).replace(/\/+$/, '');

const BUCKET_PREFIX = `${SUPABASE_URL}/storage/v1/object/public/builder-logos/`;
// 2026-02-20: Cache-bust suffix appended to every canonical logo URL so
// browsers (and our own service worker) fetch the freshest PNG after
// the partner-logo refresh, instead of serving a stale copy from disk.
// Bump this string after every Supabase Storage rotation.
const CANONICAL_LOGO_VERSION = 'v=2026022001';

const KNOWN_SLUGS = new Set([
  'alete-homes',
  'ap-designer-homes',
  'australian-building-co-sa',
  'belroc-homes',
  'bradford',
  'buildscope',
  'fairmont-homes',
  'format-homes',
  'homecorp-qld',
  'homecorp-sa',
  'homecorp-vic',
  'hpg',
  'incredible-homes',
  'kentbuilt',
  'la-vida-homes-sa',
  'lofty',
  'ls-design-and-construct',
  'metricon-sa',
  'ora-projects',
  'orizon-homes',
  'orne-renovations',
  'regent-homes',
  'rossdale',
  'scott-salisbury',
  'simonds-homes-nsw',
  'simonds-homes-qld',
  'simonds-homes-sa',
  'simonds-homes-vic',
  'st-construct',
  'urban-edge-sa',
  'urban3',
]);

// Legacy / alias slugs that other code/localStorage may still use. Map
// them to the canonical slug above so resolution works regardless of
// which historical form the caller has.
const SLUG_ALIASES = {
  // 2026-02 rebranding
  'australian-building-co': 'australian-building-co-sa',
  'lofty-building-group': 'lofty',
  'hpg-homes': 'hpg',
  'metricon': 'metricon-sa',
  'simonds': 'simonds-homes-sa',
  'simonds-homes': 'simonds-homes-sa',
  'homecorp': 'homecorp-sa',
  'urban-edge': 'urban-edge-sa',
  'la-vida-homes': 'la-vida-homes-sa',
  'la-vida': 'la-vida-homes-sa',
  'lavida': 'la-vida-homes-sa',
  'urban-3': 'urban3',
};

function canonicalSlug(input) {
  if (!input) return null;
  const s = String(input).toLowerCase().trim();
  if (KNOWN_SLUGS.has(s)) return s;
  if (SLUG_ALIASES[s]) return SLUG_ALIASES[s];
  // Try slugifying business names like "Urban Edge SA" → "urban-edge-sa"
  const slug = s
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (KNOWN_SLUGS.has(slug)) return slug;
  if (SLUG_ALIASES[slug]) return SLUG_ALIASES[slug];
  return null;
}

/**
 * Resolve the correct logo URL for a builder row.
 *
 * Resolution order:
 *   1. If we recognise the builder's slug (or business_name → slug), return
 *      its canonical Supabase URL - this always wins so a freshly uploaded
 *      partner logo replaces any stale snapshot in localStorage.
 *   2. Otherwise, fall back to whatever logo_url the caller already has.
 *
 * @param {object|string} builder    Builder row {slug, builder_slug, logo_url, business_name, builder_name}
 *                                   OR a slug string.
 * @param {string} [fallbackLogoUrl] Explicit fallback when builder is a slug string.
 * @returns {string|null}
 */
export function resolveBuilderLogo(builder, fallbackLogoUrl) {
  if (!builder) return fallbackLogoUrl || null;

  if (typeof builder === 'string') {
    const slug = canonicalSlug(builder);
    return slug ? `${BUCKET_PREFIX}${slug}.png?${CANONICAL_LOGO_VERSION}` : (fallbackLogoUrl || null);
  }

  // Try the explicit slug fields, then the business_name fallback.
  const slug =
    canonicalSlug(builder.slug) ||
    canonicalSlug(builder.builder_slug) ||
    canonicalSlug(builder.business_name) ||
    canonicalSlug(builder.builder_name) ||
    canonicalSlug(builder.name);

  if (slug) return `${BUCKET_PREFIX}${slug}.png?${CANONICAL_LOGO_VERSION}`;

  return builder.logo_url || builder.logo || fallbackLogoUrl || null;
}

/**
 * Quick boolean check for "do we have a canonical Supabase logo for this slug?"
 * Useful when conditionally setting `loading="eager"` (canonical logos are CDN-cached).
 */
export function hasBuilderLogoOverride(slug) {
  return Boolean(canonicalSlug(slug));
}

export default resolveBuilderLogo;
